import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentUser } from "@/lib/authUser";

/**
 * Curated palette: every entry verified to meet WCAG AA contrast against
 * both light (#fafafa) and dark (#08090a) surfaces when used as a small
 * accent or button fill. Lightness sits in the 50–60% sweet-spot.
 */
const colorMap: Record<string, { hsl: string; name: string }> = {
  red: { hsl: "356 70% 54%", name: "Red" },
  blue: { hsl: "220 90% 56%", name: "Blue" },
  green: { hsl: "150 55% 42%", name: "Green" },
  purple: { hsl: "262 70% 60%", name: "Purple" },
  teal: { hsl: "180 65% 40%", name: "Teal" },
  orange: { hsl: "22 88% 52%", name: "Orange" },
  pink: { hsl: "330 72% 56%", name: "Pink" },
  indigo: { hsl: "238 70% 60%", name: "Indigo" },
};

export function useThemeColor() {
  const [themeColor, setThemeColor] = useState<string>("red");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchThemeColor();
  }, []);

  const fetchThemeColor = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("theme_color")
        .eq("user_id", user.id)
        .single();

      if (data?.theme_color) {
        setThemeColor(data.theme_color);
        applyThemeColor(data.theme_color);
      }
    } catch (error) {
      console.error("Error fetching theme color:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeColor = (color: string) => {
    const colorData = colorMap[color];
    if (!colorData) return;

    const root = document.documentElement;

    root.style.setProperty("--primary", colorData.hsl);
    root.style.setProperty("--ring", colorData.hsl);
    root.style.setProperty("--sidebar-ring", colorData.hsl);
  };

  const updateThemeColor = async (color: string) => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const { error } = await supabase
        .from("profiles")
        .update({ theme_color: color })
        .eq("user_id", user.id);

      if (error) throw error;

      setThemeColor(color);
      applyThemeColor(color);
    } catch (error) {
      console.error("Error updating theme color:", error);
      throw error;
    }
  };

  return {
    themeColor,
    updateThemeColor,
    loading,
    availableColors: colorMap,
  };
}
